import type { Position } from 'geojson';
import type { TelemetrySeries } from './telemetry-series';
import { BODY_IDS, FLIGHT_PHASES, REFERENCE_FRAMES, findIndexForTime } from './telemetry-series';

export interface GroundTrackSample {
  index: number;
  fraction: number;
  /** Interpolated absolute epoch used to keep moving bodies in sync. */
  epoch: number;
  latitudeDeg: number;
  longitudeDeg: number;
  altitudeMeters: number;
  speedMetersPerSecond: number;
  centralBodyId: (typeof BODY_IDS)[number];
  referenceFrame: (typeof REFERENCE_FRAMES)[number];
  phase: (typeof FLIGHT_PHASES)[number];
  /** Last Earth-fixed sample used to draw the ground track. */
  earthTrackIndex: number;
  offEarth: boolean;
}

export function normalizeLongitude(longitudeDeg: number): number {
  const wrapped = ((((longitudeDeg + 180) % 360) + 360) % 360) - 180;
  return wrapped === -180 ? 180 : wrapped;
}

export function interpolateLongitude(fromDeg: number, toDeg: number, fraction: number): number {
  const shortestDelta = normalizeLongitude(toDeg - fromDeg);
  return normalizeLongitude(fromDeg + shortestDelta * fraction);
}

function lerp(from: number, to: number, fraction: number): number {
  return from + (to - from) * fraction;
}

/**
 * Interpolates only the visualization between two deterministic simulation
 * samples. It does not feed values back into the physics or persisted result.
 */
export function interpolateGroundTrack(
  series: TelemetrySeries,
  timeSeconds: number,
): GroundTrackSample | null {
  const index = findIndexForTime(series.t, timeSeconds);
  if (index < 0) return null;

  const nextIndex = Math.min(index + 1, series.t.length - 1);
  const interval = series.t[nextIndex]! - series.t[index]!;
  const sameFrame =
    series.centralBodyCode[nextIndex] === series.centralBodyCode[index] &&
    series.referenceFrameCode[nextIndex] === series.referenceFrameCode[index];
  const fraction =
    nextIndex === index || interval <= 0 || !sameFrame
      ? 0
      : Math.min(1, Math.max(0, (timeSeconds - series.t[index]!) / interval));

  const centralBodyId = BODY_IDS[series.centralBodyCode[index]!] ?? 'earth';
  const referenceFrame = REFERENCE_FRAMES[series.referenceFrameCode[index]!] ?? 'earth-inertial';
  const phase = FLIGHT_PHASES[series.phaseCode[index]!] ?? 'pre-launch';
  let earthTrackIndex = index;
  while (
    earthTrackIndex > 0 &&
    (series.centralBodyCode[earthTrackIndex] !== 0 ||
      series.referenceFrameCode[earthTrackIndex] !== 0)
  ) {
    earthTrackIndex -= 1;
  }
  const offEarth = centralBodyId !== 'earth' || referenceFrame !== 'earth-inertial';

  return {
    index,
    fraction,
    epoch: lerp(series.epoch[index]!, series.epoch[nextIndex]!, fraction),
    latitudeDeg: lerp(series.latitudeDeg[index]!, series.latitudeDeg[nextIndex]!, fraction),
    longitudeDeg: interpolateLongitude(
      series.longitudeDeg[index]!,
      series.longitudeDeg[nextIndex]!,
      fraction,
    ),
    altitudeMeters: lerp(series.altitude[index]!, series.altitude[nextIndex]!, fraction),
    speedMetersPerSecond: lerp(series.speed[index]!, series.speed[nextIndex]!, fraction),
    centralBodyId,
    referenceFrame,
    phase,
    earthTrackIndex,
    offEarth,
  };
}

/** Split lines that cross ±180° so canvas renderers never draw across the map. */
export function splitAtAntimeridian(coordinates: Position[]): Position[][] {
  if (coordinates.length === 0) return [];
  const lines: Position[][] = [[coordinates[0]!]];

  for (let index = 1; index < coordinates.length; index += 1) {
    const previous = coordinates[index - 1]!;
    const current = coordinates[index]!;
    if (Math.abs(current[0]! - previous[0]!) > 180) lines.push([]);
    lines[lines.length - 1]!.push(current);
  }

  return lines.filter((line) => line.length > 0);
}

export function groundTrackCoordinates(
  series: TelemetrySeries,
  throughIndex = series.t.length - 1,
): Position[] {
  const coordinates: Position[] = [];
  const last = Math.min(throughIndex, series.t.length - 1);
  for (let index = 0; index <= last; index += 1) {
    if (series.centralBodyCode[index] !== 0 || series.referenceFrameCode[index] !== 0) continue;
    coordinates.push([series.longitudeDeg[index]!, series.latitudeDeg[index]!]);
  }
  return coordinates;
}
